import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Telescope, ChevronUp, ShieldAlert, ExternalLink } from 'lucide-react';

// Collapsible pre-call research summary shown next to the live transcript.
export const ResearchPeekPanel = ({ research }) => {
  const [open, setOpen] = useState(false);
  if (!research?.profile) return null;
  const p = research.profile;
  const snap = p.company_snapshot || {};
  const contact = p.contact_profile || {};
  const challenges = (p.challenges_and_fit || []).slice(0, 3);
  const metrics = (p.custom_metrics || []).slice(0, 4);
  const questions = (p.discovery_questions || []).slice(0, 3);

  return (
    <div className="border-t border-zinc-200" data-testid="research-peek-panel">
      <div
        className="px-4 py-2 flex items-center justify-between bg-teal-50/60 cursor-pointer hover:bg-teal-50 transition-colors"
        onClick={() => setOpen((v) => !v)}
        data-testid="research-peek-toggle"
      >
        <div className="flex items-center gap-2">
          <Telescope className="w-4 h-4 text-brand-primary" />
          <span className="text-xs font-medium text-zinc-700">
            Pre-call research — {research.prospect_company}
          </span>
        </div>
        <ChevronUp className={`w-4 h-4 text-zinc-400 transition-transform ${open ? '' : 'rotate-180'}`} />
      </div>

      {open && (
        <div className="max-h-64 overflow-y-auto px-4 py-3 bg-white border-t border-zinc-100 space-y-3 text-sm" data-testid="research-peek-content">
          {snap.summary && <p className="text-zinc-700 text-xs leading-relaxed">{snap.summary}</p>}

          {metrics.length > 0 && (
            <div className="space-y-1.5">
              {metrics.map((m, i) => (
                <div key={i} className="text-xs">
                  <span className="font-semibold text-zinc-600">{m.metric}:</span>{' '}
                  <span className="text-zinc-600">{m.finding}</span>
                </div>
              ))}
            </div>
          )}

          {challenges.length > 0 && (
            <div className="space-y-1.5">
              {challenges.map((c, i) => (
                <div key={i} className="text-xs bg-zinc-50 rounded-lg p-2">
                  <span className="text-zinc-700">{c.challenge}</span>
                  <div className="text-emerald-700 mt-0.5"><span className="font-semibold">Angle:</span> {c.our_angle}</div>
                </div>
              ))}
            </div>
          )}

          {(contact.name || research.contact_name) && (
            <p className="text-xs text-zinc-600">
              <span className="font-semibold">Meeting:</span> {contact.name || research.contact_name}
              {(contact.title || research.contact_title) && ` — ${contact.title || research.contact_title}`}
              {contact.tenure_note && contact.tenure_note !== 'Not publicly available' && ` · ${contact.tenure_note}`}
            </p>
          )}

          {questions.length > 0 && (
            <div className="space-y-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Prepped questions</span>
              {questions.map((q, i) => (
                <p key={i} className="text-xs text-zinc-600">• {q}</p>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between pt-1 border-t border-zinc-100">
            <span className="flex items-center gap-1 text-[10px] text-amber-600/90">
              <ShieldAlert className="w-3 h-3" /> AI-generated, indicative — verify key facts
            </span>
            <Link to={`/research?open=${research.id}`} target="_blank" className="flex items-center gap-1 text-[10px] font-medium text-brand-primary hover:underline" data-testid="research-peek-full-link">
              Full research <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResearchPeekPanel;
